import { getNavItemApi, getNavItemsApi } from '@/api-calls/nav-items/get'
import {
  createNavItemApi,
  updateNavItemApi,
} from '@/api-calls/nav-items/mutate'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import { useSite } from '@/hooks/use-site'
import { SLUG_DOMAIN } from '@/lib/config'
import {
  createNavItemSchema,
  updateNavItemSchema,
} from '@/lib/schema/nav-items'
import { zodResolver } from '@hookform/resolvers/zod'
import { toRelativeSiteUrl } from '@blogging/utils'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Skeleton,
} from '@blogging/ui'
import { useFetch, useMutation } from '@ngtrvu/use-api'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

// The DB default for `group` predates the header/footer enum (legacy rows
// may carry "main" or null) — always coerce to a valid location so the
// Location select never renders blank.
const normalizeGroup = (group?: string | null): 'header' | 'footer' =>
  group === 'footer' ? 'footer' : 'header'

interface NavItemFormProps {
  siteId: string
  id?: string
  isNew: boolean
  navItem: any
  allNavItems: any[]
}

// Only mounts once its data has actually loaded, so `useForm`'s
// `defaultValues` are correct from this component's very first render.
// Populating the form later via `form.reset()` (after mounting with
// placeholder defaults) made the "Parent Item" select's value change very
// shortly after mount — which raced against Radix Select's internal hidden
// native <select> (used for browser autofill/native form compat) and
// intermittently left it desynced, showing blank until a full page reload.
// Mounting fresh with the right values sidesteps that race entirely.
function NavItemForm({ siteId, id, isNew, navItem, allNavItems }: NavItemFormProps) {
  const navigate = useNavigate()
  const { site } = useSite()

  const form = useForm({
    resolver: zodResolver(isNew ? createNavItemSchema : updateNavItemSchema),
    defaultValues: {
      title: navItem?.title || '',
      url: navItem?.url || '',
      group: normalizeGroup(navItem?.group),
      description: navItem?.description || '',
      status: navItem?.status || 'published',
      parentId: navItem?.parentId || (null as string | null),
    },
  })

  const selectedGroup = normalizeGroup(form.watch('group'))
  const currentParentId = form.watch('parentId')

  // Only top-level items in the same location can be parents (no nested
  // nesting). Always keep the currently selected parent in the list too,
  // in case the sibling list doesn't include it (e.g. a legacy group
  // mismatch) — the single-item fetch already resolves the `parent`
  // relation itself, so fall back to that.
  const parentOptions = (allNavItems || []).filter(
    (item: any) =>
      !item.parentId &&
      item.id !== id &&
      (normalizeGroup(item.group) === selectedGroup ||
        item.id === currentParentId),
  )
  if (
    navItem?.parent &&
    currentParentId === navItem.parent.id &&
    !parentOptions.some((item: any) => item.id === navItem.parent.id)
  ) {
    parentOptions.push(navItem.parent)
  }

  // Site's own domains — used to reduce a pasted absolute URL down to a
  // relative path (e.g. https://acme.blgg.dev/about -> /about).
  const siteHosts = [
    site?.domainVerified ? site?.domain : null,
    site?.slug ? `${site.slug}${SLUG_DOMAIN}` : null,
  ]
  const normalizeUrl = (url: string) => toRelativeSiteUrl(url, siteHosts)

  const { mutate: createNavItem, isPending: isCreating } = useMutation(
    createNavItemApi,
    {
      resourceName: 'navItem',
      onSuccess: () => {
        toast.success('Navigation item created!')
        navigate(`/${siteId}/settings/nav`)
      },
      onError: (error: any) => {
        toast.error(error?.data?.error || error?.message || 'Failed to create navigation item')
      },
    },
  )

  const { mutate: updateNavItem, isPending: isUpdating } = useMutation(
    updateNavItemApi,
    {
      resourceName: 'navItem',
      onSuccess: () => {
        toast.success('Navigation item updated!')
        navigate(`/${siteId}/settings/nav`)
      },
      onError: (error: any) => {
        toast.error(error?.data?.error || error?.message || 'Failed to update navigation item')
      },
    },
  )

  const onSubmit = (data: Record<string, unknown>) => {
    const payload = {
      ...data,
      url: typeof data.url === 'string' ? normalizeUrl(data.url) : data.url,
    }
    if (isNew) {
      createNavItem({ siteId, ...payload })
    } else {
      updateNavItem({ id, siteId, ...payload })
    }
  }

  const isSubmitting = isCreating || isUpdating

  return (
    <Card>
      <CardHeader>
        <CardTitle>Item Details</CardTitle>
        <CardDescription>
          Configure the details for this navigation item.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="col-span-2 md:col-span-1">
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Home"
                        {...field}
                        value={field.value as string}
                      />
                    </FormControl>
                    <FormDescription>
                      The text displayed in the menu.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="url"
                render={({ field }) => (
                  <FormItem className="col-span-2 md:col-span-1">
                    <FormLabel>URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="/about or https://example.com"
                        {...field}
                        value={field.value as string}
                        onBlur={(e) => {
                          field.onChange(normalizeUrl(e.target.value))
                          field.onBlur()
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      Destination link (e.g., /about). Links to your own
                      site are stored as a relative path.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="group"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value)
                        // A parent only makes sense within its own
                        // location — drop it if it no longer applies.
                        const currentParentId = form.getValues('parentId')
                        if (!currentParentId) return
                        const stillValid = (allNavItems || []).some(
                          (item: any) =>
                            item.id === currentParentId &&
                            !item.parentId &&
                            normalizeGroup(item.group) === value,
                        )
                        if (!stillValid) {
                          form.setValue('parentId', null)
                        }
                      }}
                      value={field.value as string}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="header">
                          Header Navigation
                        </SelectItem>
                        <SelectItem value="footer">
                          Footer Navigation
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Where this item appears.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="parentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parent Item (Optional)</FormLabel>
                    <Select
                      onValueChange={(v) =>
                        field.onChange(v === '__none__' ? null : v)
                      }
                      value={(field.value as string | null) ?? '__none__'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Top-level item" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">
                          None (top-level)
                        </SelectItem>
                        {parentOptions.map((parent: any) => (
                          <SelectItem key={parent.id} value={parent.id}>
                            {parent.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Nest this item under a top-level item in the same
                      location to create a dropdown.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value as string}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Visibility of the item.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="A brief description..."
                        {...field}
                        value={field.value as string}
                        rows={3}
                      />
                    </FormControl>
                    <FormDescription>
                      Shown as a subtitle in dropdown menus.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isNew ? 'Create Item' : 'Save Changes'}
              </Button>
              <Link to={`/${siteId}/settings/nav`}>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}

export function SiteSettingsNavItemPage() {
  const { siteId, id } = useParams()
  const isNew = !id

  const { data: allNavItems, isLoading: isLoadingNavItems } = useFetch(
    getNavItemsApi,
    {
      resourceName: 'navItems',
      initialParams: { siteId: siteId as string },
    },
  )

  const { data: navItem, isLoading: isLoadingItem } = useFetch(getNavItemApi, {
    resourceName: 'navItem',
    initialParams: { siteId: siteId as string, id: id as string },
    autoLoad: !isNew && !!id,
  })

  const isLoading = !isNew && (isLoadingItem || isLoadingNavItems)

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-8">
        <div className="flex items-center gap-4">
          <Link to={`/${siteId}/settings/nav`}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold">
            {isNew ? 'New Navigation Item' : 'Edit Navigation Item'}
          </h1>
          <p className="text-muted-foreground">
            {isNew
              ? 'Add a new navigation item to your site.'
              : 'Edit an existing navigation item.'}
          </p>
        </div>

        {isLoading ? (
          <Skeleton className="h-48 w-full rounded-lg" />
        ) : (
          <NavItemForm
            key={isNew ? 'new' : id}
            siteId={siteId as string}
            id={id}
            isNew={isNew}
            navItem={navItem}
            allNavItems={allNavItems || []}
          />
        )}
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}
